import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ProductTile from "@/components/store/ProductTile";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Building2,
  Layers,
  DollarSign,
  MapPin,
  Ruler,
  Check,
  Save,
  Package,
  Send,
  Coffee,
  Utensils,
  ShoppingBag,
  ArrowUpRight,
} from "lucide-react";

/**
 * BOM Builder — a 5-step wizard that generates a Bill of Materials.
 * Each step is a full-page panel so it's usable on both mobile and desktop.
 */

const STEPS = [
  { key: "country", label: "Country", icon: MapPin },
  { key: "store_type", label: "Store type", icon: Building2 },
  { key: "area", label: "Total area", icon: Ruler },
  { key: "budget", label: "Budget", icon: DollarSign },
  { key: "locations", label: "Locations", icon: Layers },
];

const STORE_TYPES = [
  { key: "coffee", label: "Coffee shop / Café", icon: Coffee },
  { key: "restaurant", label: "Restaurant / QSR", icon: Utensils },
  { key: "boutique", label: "Boutique / Fashion", icon: ShoppingBag },
  { key: "retail", label: "Retail store", icon: ShoppingBag },
  { key: "salon", label: "Salon / Spa", icon: Sparkles },
  { key: "gym", label: "Gym / Fitness", icon: Building2 },
  { key: "clinic", label: "Clinic / Healthcare", icon: Building2 },
  { key: "corporate", label: "Corporate / Office", icon: Building2 },
  { key: "hotel", label: "Hotel / Hospitality", icon: Building2 },
  { key: "other", label: "Other / Custom", icon: Building2 },
];

const BUDGET_BANDS = [
  { key: "starter", label: "Starter", range_inr: "Up to ₹5L", range_usd: "Up to $5k", note: "Budget-tier hardware." },
  { key: "growth", label: "Growth", range_inr: "₹5L – ₹15L", range_usd: "$5k – $15k", note: "Mainstream commercial-grade." },
  { key: "professional", label: "Professional", range_inr: "₹15L – ₹50L", range_usd: "$15k – $50k", note: "Professional AV, 24/7 rated." },
  { key: "enterprise", label: "Enterprise", range_inr: "₹50L+", range_usd: "$50k+", note: "Enterprise-tier, multi-vendor." },
];

const COUNTRIES = [
  { key: "IN", label: "India" },
  { key: "US", label: "United States" },
  { key: "GB", label: "United Kingdom" },
  { key: "AE", label: "United Arab Emirates" },
  { key: "AU", label: "Australia" },
  { key: "SG", label: "Singapore" },
  { key: "GLOBAL", label: "Other / Global" },
];

export default function BOMBuilder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    country: "IN",
    store_type: "coffee",
    area_sqft: 800,
    budget_band: "growth",
    locations: 1,
    prefer_mirrorloops_compat: true,
  });
  const [bom, setBom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quote, setQuote] = useState({
    name: "", email: "", company: "", country: "", city: "",
    timeline: "", installation_required: false, notes: "",
  });
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [savedId, setSavedId] = useState(null);
  const nav = useNavigate();

  const canNext = useMemo(() => {
    if (step === 0) return !!answers.country;
    if (step === 1) return !!answers.store_type;
    if (step === 2) return answers.area_sqft >= 50;
    if (step === 3) return !!answers.budget_band;
    if (step === 4) return answers.locations >= 1;
    return true;
  }, [step, answers]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/store/bom/build", answers);
      setBom(r.data);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the BOM.");
    } finally {
      setBusy(false);
    }
  };

  const updateQty = (secIdx, itemIdx, next) => {
    const nextBom = { ...bom, sections: [...bom.sections] };
    const sec = { ...nextBom.sections[secIdx] };
    const items = [...sec.items];
    const it = { ...items[itemIdx] };
    it.quantity = Math.max(0, parseInt(next || 0, 10));
    it.line_total = it.unit_estimate * it.quantity;
    items[itemIdx] = it;
    sec.items = items;
    nextBom.sections[secIdx] = sec;
    nextBom.grand_total_estimate = nextBom.sections.reduce(
      (t, s) => t + s.items.reduce((tt, i) => tt + (i.line_total || 0), 0),
      0,
    );
    setBom(nextBom);
  };

  const save = async () => {
    if (!email) {
      toast.error("Email is required to save the BOM.");
      return;
    }
    try {
      const r = await api.post("/store/bom/save", {
        input: bom.input,
        sections: bom.sections,
        grand_total_estimate: bom.grand_total_estimate,
        currency: bom.currency,
        label: label || `${answers.store_type} · ${answers.country}`,
        email,
      });
      setSavedId(r.data.bom_id);
      toast.success("BOM saved — shareable link generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    }
  };

  const submitAutoQuote = async () => {
    if (!quote.name || !quote.email) {
      toast.error("Name + email are required.");
      return;
    }
    setQuoteBusy(true);
    try {
      const r = await api.post("/store/bom/auto-quote-inline", {
        requester: quote,
        sections: bom.sections,
        label: `${answers.store_type} · ${answers.country}`,
      });
      setQuoteResult(r.data);
      toast.success(`Ticket #${r.data.ticket_id.slice(0, 8)} · ${r.data.lines_created} lines quoted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit auto-quote.");
    } finally {
      setQuoteBusy(false);
    }
  };

  const currencySymbol = { INR: "₹", USD: "$", GBP: "£", AED: "AED", AUD: "AU$", SGD: "S$" };

  return (
    <PublicShell>
      <div data-testid="bom-builder" className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        {/* Header */}
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-2 flex items-center gap-2">
            <Sparkles size={12} /> Build My Store · BOM Builder
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white">
            Answer 5 questions.
            <br />
            Get a <span className="text-[#D4B878]">shoppable Bill of Materials.</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Curated across storefront, interior, commerce and infrastructure — sized to your
            area, budget and location count. Every line item is a real product from the
            Mirrorloops Store, editable and quotable.
          </p>
        </div>

        {/* Progress rail */}
        {step < 5 && (
          <div className="flex items-center gap-2 mb-10" data-testid="bom-progress">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <React.Fragment key={s.key}>
                  <div
                    data-testid={`bom-step-pill-${i}`}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-mono transition-all ${
                      i < step
                        ? "bg-[#D4B878]/15 text-[#D4B878]"
                        : i === step
                        ? "bg-[#D4B878] text-black"
                        : "border border-white/10 text-white/40"
                    }`}
                  >
                    <Icon size={11} /> {s.label}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-px bg-white/10" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Step body */}
        <div className="min-h-[300px]">
          {step === 0 && (
            <StepContainer title="Which country will these stores be in?" testid="bom-step-country">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {COUNTRIES.map((c) => (
                  <OptionCard
                    key={c.key}
                    active={answers.country === c.key}
                    onClick={() => setAnswers({ ...answers, country: c.key })}
                    testid={`bom-country-${c.key}`}
                    title={c.label}
                    subtitle={c.key}
                  />
                ))}
              </div>
            </StepContainer>
          )}
          {step === 1 && (
            <StepContainer title="What are you building?" testid="bom-step-type">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {STORE_TYPES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <OptionCard
                      key={s.key}
                      active={answers.store_type === s.key}
                      onClick={() => setAnswers({ ...answers, store_type: s.key })}
                      testid={`bom-type-${s.key}`}
                      title={s.label}
                      icon={<Icon size={20} />}
                    />
                  );
                })}
              </div>
            </StepContainer>
          )}
          {step === 2 && (
            <StepContainer title="Roughly what's the total floor area?" testid="bom-step-area">
              <div className="max-w-lg">
                <div className="text-6xl font-bold text-[#D4B878] font-mono mb-3">
                  {answers.area_sqft.toLocaleString()}
                  <span className="text-lg text-white/50 ml-2 font-sans">sq ft</span>
                </div>
                <input
                  type="range"
                  min="100"
                  max="20000"
                  step="50"
                  data-testid="bom-area-slider"
                  value={answers.area_sqft}
                  onChange={(e) => setAnswers({ ...answers, area_sqft: parseInt(e.target.value, 10) })}
                  className="w-full accent-[#D4B878]"
                />
                <div className="flex justify-between text-[10px] text-white/40 font-mono mt-1">
                  <span>100 sq ft</span>
                  <span>20,000 sq ft</span>
                </div>
                <div className="mt-4 text-xs text-white/50">
                  Storefront items scale ~every 500 sq ft; interior items ~every 300 sq ft.
                </div>
              </div>
            </StepContainer>
          )}
          {step === 3 && (
            <StepContainer title="What's your budget per location?" testid="bom-step-budget">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BUDGET_BANDS.map((b) => (
                  <OptionCard
                    key={b.key}
                    active={answers.budget_band === b.key}
                    onClick={() => setAnswers({ ...answers, budget_band: b.key })}
                    testid={`bom-budget-${b.key}`}
                    title={b.label}
                    subtitle={answers.country === "IN" ? b.range_inr : b.range_usd}
                    footer={b.note}
                  />
                ))}
              </div>
            </StepContainer>
          )}
          {step === 4 && (
            <StepContainer title="How many locations are you rolling out?" testid="bom-step-locations">
              <div className="max-w-md">
                <div className="text-6xl font-bold text-[#D4B878] font-mono mb-3">
                  {answers.locations}
                </div>
                <input
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  data-testid="bom-locations-slider"
                  value={answers.locations}
                  onChange={(e) => setAnswers({ ...answers, locations: parseInt(e.target.value, 10) })}
                  className="w-full accent-[#D4B878]"
                />
                <div className="flex justify-between text-[10px] text-white/40 font-mono mt-1">
                  <span>1</span>
                  <span>100+</span>
                </div>
              </div>
              <label className="mt-6 flex items-center gap-2 text-xs text-white/70">
                <input
                  type="checkbox"
                  data-testid="bom-compat-toggle"
                  checked={answers.prefer_mirrorloops_compat}
                  onChange={(e) => setAnswers({ ...answers, prefer_mirrorloops_compat: e.target.checked })}
                />
                Prefer Mirrorloops-compatible hardware where possible
              </label>
            </StepContainer>
          )}

          {/* Result */}
          {step === 5 && bom && (
            <div data-testid="bom-result">
              <div className="border border-[#D4B878]/25 bg-gradient-to-br from-[#D4B878]/[0.06] to-transparent rounded-lg p-6 mb-8">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-2">
                  Your Bill of Materials · ballpark estimate
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-white font-mono">
                  {currencySymbol[bom.currency] || bom.currency}
                  {bom.grand_total_estimate.toLocaleString()}
                </div>
                <div className="text-xs text-white/60 mt-2 leading-relaxed max-w-xl">
                  {bom.notes}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    onClick={() => setSaveOpen(true)}
                    data-testid="bom-save-btn"
                    className="rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold"
                  >
                    <Save size={14} className="mr-2" /> Save + share
                  </Button>
                  <Button
                    onClick={() => setQuoteOpen(true)}
                    data-testid="bom-auto-quote-btn"
                    className="rounded-full bg-[#CCFF00] hover:bg-[#E8FF66] text-black font-semibold"
                  >
                    <Send size={14} className="mr-2" /> Quote every line
                  </Button>
                  <Button
                    onClick={() => {
                      setStep(0);
                      setBom(null);
                    }}
                    variant="outline"
                    data-testid="bom-restart-btn"
                    className="rounded-full border-white/15 text-white/80"
                  >
                    Start over
                  </Button>
                </div>
              </div>

              {bom.sections.map((sec, secIdx) => (
                <section
                  key={sec.key}
                  data-testid={`bom-section-${sec.key}`}
                  className="mb-8"
                >
                  <div className="flex items-baseline justify-between mb-1 gap-3">
                    <h2 className="text-xl font-semibold text-white">{sec.name}</h2>
                    <div className="text-[11px] text-white/50 font-mono">
                      {sec.items.reduce((t, i) => t + (i.line_total || 0), 0).toLocaleString()}
                      {" "}
                      {bom.currency}
                    </div>
                  </div>
                  <p className="text-xs text-white/50 mb-3">{sec.blurb}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sec.items.map((item, itemIdx) => (
                      <div
                        key={item.product.id}
                        data-testid={`bom-item-${item.product.id}`}
                        className="border border-white/10 bg-white/[0.02] rounded-lg p-3 flex gap-3"
                      >
                        <div className="w-20 h-20 shrink-0">
                          <ProductTile p={item.product} size="sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] uppercase tracking-[0.18em] text-[#D4B878] font-semibold mb-0.5 truncate">
                            {item.product.manufacturer}
                          </div>
                          <Link
                            to={`/store/product/${item.product.id}`}
                            className="text-sm font-medium text-white hover:text-[#E8D6A3] leading-tight block mb-2 line-clamp-2"
                          >
                            {item.product.name}
                          </Link>
                          <div className="flex items-center gap-2 text-[11px]">
                            <label className="text-white/40 uppercase tracking-[0.14em] text-[9px]">qty</label>
                            <input
                              type="number"
                              min="0"
                              max="9999"
                              value={item.quantity}
                              onChange={(e) => updateQty(secIdx, itemIdx, e.target.value)}
                              data-testid={`bom-qty-${item.product.id}`}
                              className="w-14 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white font-mono text-xs"
                            />
                            <span className="ml-auto text-white/50 font-mono">
                              {currencySymbol[bom.currency] || bom.currency}
                              {item.line_total.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {sec.items.length === 0 && (
                      <div className="text-xs text-white/40 italic p-3 border border-dashed border-white/10 rounded">
                        No products matched this section for the selected filters.
                      </div>
                    )}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        {step < 5 && (
          <div className="mt-10 flex items-center justify-between">
            <Button
              onClick={() => setStep(Math.max(0, step - 1))}
              variant="outline"
              disabled={step === 0}
              data-testid="bom-prev-btn"
              className="border-white/10 text-white/70 rounded-full disabled:opacity-30"
            >
              <ChevronLeft size={14} className="mr-1" /> Back
            </Button>
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext}
                data-testid="bom-next-btn"
                className="rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold px-8"
              >
                Continue <ChevronRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button
                onClick={generate}
                disabled={!canNext || busy}
                data-testid="bom-generate-btn"
                className="rounded-full bg-[#CCFF00] hover:bg-[#E8FF66] text-black font-semibold px-8"
              >
                {busy ? "Building…" : "Build my BOM →"}
              </Button>
            )}
          </div>
        )}

        {/* Save dialog */}
        {saveOpen && (
          <div
            data-testid="bom-save-dialog"
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setSaveOpen(false)}
          >
            <div
              className="bg-[#141110] border border-white/15 rounded-lg max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              {savedId ? (
                <div className="text-center">
                  <Check size={40} className="mx-auto text-[#CCFF00] mb-3" />
                  <div className="text-white font-semibold mb-1">BOM saved.</div>
                  <div className="text-xs text-white/60 mb-4 font-mono">ID: {savedId.slice(0, 12)}</div>
                  <Button
                    onClick={() => setSaveOpen(false)}
                    data-testid="bom-save-close"
                    className="rounded-full bg-[#D4B878] text-black"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-2">
                    Save + share
                  </div>
                  <div className="text-lg font-semibold text-white mb-4">
                    Send me a shareable link
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                        Email
                      </Label>
                      <Input
                        data-testid="bom-save-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                        Label (optional)
                      </Label>
                      <Input
                        data-testid="bom-save-label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g. Cafe Bengaluru — Q2 rollout"
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <Button
                      onClick={save}
                      disabled={!email}
                      data-testid="bom-save-submit"
                      className="w-full rounded-full bg-[#CCFF00] text-black font-semibold disabled:opacity-40"
                    >
                      <Send size={13} className="mr-2" /> Save BOM
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {/* Auto-quote dialog */}
        {quoteOpen && (
          <div
            data-testid="bom-quote-dialog"
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => !quoteBusy && setQuoteOpen(false)}
          >
            <div
              className="bg-[#141110] border border-white/15 rounded-lg max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              {quoteResult ? (
                <div className="text-center" data-testid="bom-quote-result">
                  <Check size={40} className="mx-auto text-[#CCFF00] mb-3" />
                  <div className="text-white font-semibold mb-1">
                    Ticket #{quoteResult.ticket_id.slice(0, 8)}
                  </div>
                  <div className="text-sm text-white/70 mb-3">
                    <span className="font-mono text-[#CCFF00]">{quoteResult.lines_created}</span> line
                    {quoteResult.lines_created === 1 ? "" : "s"} sent to procurement — we respond within 1 business day.
                  </div>
                  {quoteResult.dispatch?.to?.length > 0 && (
                    <div
                      data-testid="bom-quote-dispatch-info"
                      className="mb-4 mx-auto max-w-sm p-3 border border-[#CCFF00]/25 rounded bg-[#CCFF00]/[0.04] text-left"
                    >
                      <div className="text-[9px] uppercase tracking-[0.22em] text-[#CCFF00] mb-1 flex items-center gap-1.5">
                        <Send size={10} />
                        {quoteResult.dispatch.dispatch?.status === "sent" ? "Email delivered" :
                         quoteResult.dispatch.dispatch?.status === "logged" ? "Email queued (dev mode)" :
                         "Email dispatched"}
                      </div>
                      <div className="text-[11px] text-white/80 font-mono break-all leading-relaxed">
                        {quoteResult.dispatch.to.join(" · ")}
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={() => {
                      setQuoteOpen(false);
                      setQuoteResult(null);
                    }}
                    data-testid="bom-quote-close"
                    className="rounded-full bg-[#D4B878] text-black"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] font-semibold mb-2">
                    Quote every line
                  </div>
                  <div className="text-lg font-semibold text-white mb-1">One ticket, every SKU</div>
                  <div className="text-xs text-white/60 mb-4">
                    Fans out a quote request to every line in your BOM — procurement gets a single parent ticket
                    they can act on in bulk.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Name</Label>
                      <Input
                        data-testid="bom-quote-name"
                        value={quote.name}
                        onChange={(e) => setQuote({ ...quote, name: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Email</Label>
                      <Input
                        data-testid="bom-quote-email"
                        type="email"
                        value={quote.email}
                        onChange={(e) => setQuote({ ...quote, email: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Company (optional)</Label>
                      <Input
                        data-testid="bom-quote-company"
                        value={quote.company}
                        onChange={(e) => setQuote({ ...quote, company: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Country</Label>
                      <Input
                        data-testid="bom-quote-country"
                        value={quote.country}
                        onChange={(e) => setQuote({ ...quote, country: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Timeline</Label>
                      <Input
                        data-testid="bom-quote-timeline"
                        value={quote.timeline}
                        onChange={(e) => setQuote({ ...quote, timeline: e.target.value })}
                        placeholder="e.g. Q2 2026"
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-xs text-white/70">
                    <input
                      type="checkbox"
                      data-testid="bom-quote-install"
                      checked={quote.installation_required}
                      onChange={(e) => setQuote({ ...quote, installation_required: e.target.checked })}
                    />
                    Installation required
                  </label>
                  <Button
                    onClick={submitAutoQuote}
                    disabled={quoteBusy || !quote.name || !quote.email}
                    data-testid="bom-quote-submit"
                    className="w-full mt-4 rounded-full bg-[#CCFF00] text-black font-semibold disabled:opacity-40"
                  >
                    <Send size={13} className="mr-2" />
                    {quoteBusy ? "Sending…" : "Send bulk quote request"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PublicShell>
  );
}

// ------ small helpers ------

function StepContainer({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.24em] text-white/40 mb-2">
        Step
      </div>
      <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-8">{title}</h2>
      {children}
    </div>
  );
}

function OptionCard({ active, onClick, testid, title, subtitle, footer, icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left border rounded-lg p-4 transition-all ${
        active
          ? "border-[#D4B878] bg-[#D4B878]/[0.08]"
          : "border-white/10 bg-white/[0.02] hover:border-white/20"
      }`}
    >
      {icon && (
        <div className={`mb-2 ${active ? "text-[#D4B878]" : "text-white/40"}`}>
          {icon}
        </div>
      )}
      <div className={`font-semibold ${active ? "text-white" : "text-white/80"}`}>
        {title}
      </div>
      {subtitle && (
        <div className="text-xs text-white/50 font-mono mt-1">{subtitle}</div>
      )}
      {footer && (
        <div className="text-[10px] text-white/40 mt-2 leading-relaxed">{footer}</div>
      )}
    </button>
  );
}
